/**
 * T8500 · Degraded-mode NATS→POLL adaptive fallback.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 *
 * When the connectivity FSM flips `CONNECTED → DEGRADED_NO_NATS`, every
 * realtime query is auto-promoted to POLL mode via a function-form
 * `refetchInterval`. The fallback respects the visibility gate (no
 * polling while `document.hidden`) and STOPS on flip back to `CONNECTED`
 * so subsequent NATS events resume the patch path naturally.
 *
 * DISTINCT from `DEGRADED_NO_REST`: that state has REST-side exhaustion
 * with the 5/10/20/40 min exponential retry handled by T8005. Here REST
 * is healthy and we substitute it for the dead NATS channel.
 */

import type { Query } from '@tanstack/react-query';
import { connectivityStore } from '../../core/connectivity-store.js';
import {
  hashResponse,
  initialPollState,
  nextPollState,
  type PollState,
} from './adaptive-poll-state.js';

const pollStates = new WeakMap<Query, PollState>();

// Platform-neutral default: assume the page is visible. Callers that
// care about visibility-gated polling pass an `isDocumentHidden`
// function backed by `adapter.appState.current()` — see
// `useRealtimeQuery`. Direct `document.hidden` access would violate
// `.claude/rules/platform-agnostic.md`.
function defaultIsDocumentHidden(): boolean {
  return false;
}

export interface DegradedPollDeps {
  /** Returns true when the document is hidden (no polling allowed). */
  readonly isDocumentHidden?: () => boolean;
}

/**
 * Function-form `refetchInterval` passed to every realtime query.
 * Returns ms while in DEGRADED_NO_NATS; returns `false` otherwise.
 */
export function degradedPollInterval(query: Query, deps: DegradedPollDeps = {}): number | false {
  if (connectivityStore.getState().state !== 'DEGRADED_NO_NATS') return false;
  const hidden = deps.isDocumentHidden ?? defaultIsDocumentHidden;
  if (hidden()) return false;
  const state = pollStates.get(query) ?? initialPollState();
  return state.intervalMs;
}

/**
 * Advance the poll state for `query` based on the latest response.
 * Caller invokes this AFTER a poll-driven refetch lands. The function
 * computes the hash and advances the ladder only while the fallback is
 * active. Healthy-session writes must not prime the next degraded episode.
 */
export function notePollResponse(query: Query, response: unknown): PollState {
  const prev = pollStates.get(query) ?? initialPollState();
  if (connectivityStore.getState().state !== 'DEGRADED_NO_NATS') return prev;
  const next = nextPollState(prev, hashResponse(response));
  pollStates.set(query, next);
  return next;
}

/**
 * Reset the poll state for `query` — called when the FSM transitions
 * back to CONNECTED so the next DEGRADED_NO_NATS episode starts at the
 * 4 s base.
 */
export function resetPollState(query: Query): void {
  pollStates.delete(query);
}

/** Test seam — clear ALL tracked poll states. */
export function _resetAllPollStatesForTest(): void {
  // WeakMap cannot be iterated. The seam exists for documentation only;
  // individual tests use distinct Query references so cross-test bleed
  // is impossible in practice.
}
